import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { A5_PORTRAIT_CAPTURE_WIDTH_PX } from './printPageStyles';

const PDF_MARGIN_MM = 5;

const computeLabelPlacement = (
  pageWidth: number,
  pageHeight: number,
  canvasWidth: number,
  canvasHeight: number
) => {
  const maxWidth = pageWidth - PDF_MARGIN_MM * 2;
  const maxHeight = pageHeight - PDF_MARGIN_MM * 2;

  let renderWidth = maxWidth;
  let renderHeight = (canvasHeight * renderWidth) / canvasWidth;

  if (renderHeight > maxHeight) {
    renderHeight = maxHeight;
    renderWidth = (canvasWidth * renderHeight) / canvasHeight;
  }

  return {
    x: (pageWidth - renderWidth) / 2,
    y: PDF_MARGIN_MM,
    renderWidth,
    renderHeight,
  };
};

export interface HepsiburadaLabelPdfOptions {
  fileName?: string;
  /** Yeni sekmede PDF açıldıktan sonra yazdırma diyaloğunu tetiklemeyi dene */
  tryAutoPrint?: boolean;
}

/**
 * Hepsiburada kargo etiketini A5 dikey (portrait) PDF olarak üretir.
 * Her etiket tek A5 sayfasına sığdırılır; çok ürünlü siparişlerde otomatik ölçeklenir.
 */
export async function exportHepsiburadaLabelToPdf(
  element: HTMLElement,
  options: HepsiburadaLabelPdfOptions = {}
): Promise<void> {
  const canvas = await html2canvas(element, {
    scale: 2.5,
    logging: false,
    useCORS: true,
    allowTaint: true,
    backgroundColor: '#ffffff',
    onclone: (clonedDoc) => {
      const clonedLabel = clonedDoc.querySelector('.hb-label-sheet');
      if (clonedLabel instanceof HTMLElement) {
        clonedLabel.style.boxShadow = 'none';
        clonedLabel.style.background = '#ffffff';
        clonedLabel.style.width = `${A5_PORTRAIT_CAPTURE_WIDTH_PX}px`;
        clonedLabel.style.maxWidth = `${A5_PORTRAIT_CAPTURE_WIDTH_PX}px`;
        clonedLabel.style.boxSizing = 'border-box';
      }
    },
  });

  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a5',
  });

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();

  const imgData = canvas.toDataURL('image/png', 1.0);
  const { x, y, renderWidth, renderHeight } = computeLabelPlacement(
    pageWidth,
    pageHeight,
    canvas.width,
    canvas.height
  );

  pdf.addImage(imgData, 'PNG', x, y, renderWidth, renderHeight, undefined, 'FAST');

  const fileName = options.fileName ?? 'Hepsiburada_Kargo_Etiketi.pdf';
  const blob = pdf.output('blob');
  const url = URL.createObjectURL(blob);

  const printWindow = window.open(url, '_blank');
  if (!printWindow) {
    pdf.save(fileName);
    URL.revokeObjectURL(url);
    throw new Error('Pop-up engelleyici nedeniyle PDF açılamadı.');
  }

  printWindow.document.title = fileName.replace(/\.pdf$/i, '');

  if (options.tryAutoPrint !== false) {
    window.setTimeout(() => {
      try {
        printWindow.focus();
        printWindow.print();
      } catch {
        // PDF viewer kendi yazdırma akışını kullanır
      }
    }, 900);
  }

  window.setTimeout(() => URL.revokeObjectURL(url), 120_000);
}

/**
 * Birden fazla Hepsiburada etiketini ardışık A5 dikey sayfalarda tek PDF'de üretir.
 */
export async function exportHepsiburadaLabelsToPdf(
  elements: HTMLElement[],
  options: HepsiburadaLabelPdfOptions = {}
): Promise<void> {
  if (elements.length === 0) return;

  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a5',
  });

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();

  for (let index = 0; index < elements.length; index += 1) {
    const element = elements[index];
    const canvas = await html2canvas(element, {
      scale: 2.5,
      logging: false,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      onclone: (clonedDoc) => {
        const clonedLabel = clonedDoc.querySelector('.hb-label-sheet');
        if (clonedLabel instanceof HTMLElement) {
          clonedLabel.style.boxShadow = 'none';
          clonedLabel.style.background = '#ffffff';
          clonedLabel.style.width = `${A5_PORTRAIT_CAPTURE_WIDTH_PX}px`;
          clonedLabel.style.maxWidth = `${A5_PORTRAIT_CAPTURE_WIDTH_PX}px`;
          clonedLabel.style.boxSizing = 'border-box';
        }
      },
    });

    if (index > 0) {
      pdf.addPage('a5', 'portrait');
    }

    const imgData = canvas.toDataURL('image/png', 1.0);
    const { x, y, renderWidth, renderHeight } = computeLabelPlacement(
      pageWidth,
      pageHeight,
      canvas.width,
      canvas.height
    );
    pdf.addImage(imgData, 'PNG', x, y, renderWidth, renderHeight, undefined, 'FAST');
  }

  const fileName = options.fileName ?? 'Hepsiburada_Kargo_Etiketleri.pdf';
  const blob = pdf.output('blob');
  const url = URL.createObjectURL(blob);

  const printWindow = window.open(url, '_blank');
  if (!printWindow) {
    pdf.save(fileName);
    URL.revokeObjectURL(url);
    throw new Error('Pop-up engelleyici nedeniyle PDF açılamadı.');
  }

  printWindow.document.title = fileName.replace(/\.pdf$/i, '');

  if (options.tryAutoPrint !== false) {
    window.setTimeout(() => {
      try {
        printWindow.focus();
        printWindow.print();
      } catch {
        // PDF viewer kendi yazdırma akışını kullanır
      }
    }, 900);
  }

  window.setTimeout(() => URL.revokeObjectURL(url), 120_000);
}
